import { useState } from "react";
import type { ExtractedBudget, ProjectPhase } from "@/lib/offer-types";
import { enrichBudgetLines, type MatchedBudgetLine } from "@/lib/match-offer-line-templates";
import type { OfferLineTemplate } from "@/hooks/use-offers";

export interface PreviewLine {
  id: string;
  phase: ProjectPhase;
  description: string;
  quantity: number;
  unit: string;
  quantity_hours: number;
  amount: number;
  cost_type: "internal" | "external";
  template_id: string | null;
  template_name: string | null;
  /** VIB-1021: tilvalg/opsjon — stemplet fra kildebudsjettets is_optional. */
  is_optional: boolean;
}

export interface PreviewMetadata {
  offer_total: number;
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  startup_invoice_pct: number;
  final_invoice_pct: number;
}

export interface BuildPreviewResult {
  lines: PreviewLine[];
  metadata: PreviewMetadata | null;
}

/**
 * Pure preview-builder. Trekker ut fra en parsed ExtractedBudget hva som skal
 * vises i preview-tabellen, inkludert mal-matching og normalisering av
 * quantity_hours (eksterne kostnader / ikke-tidsbaserte enheter nullstilles).
 *
 * Eksportert separat så vi kan unit-teste uten å mounte React.
 */
/** Budsjett-felter buildPreviewFromMatchedBudget trenger (MatchedBudget oppfyller den). */
export interface MatchedBudgetInput {
  lines: MatchedBudgetLine[];
  /** Budsjett-nivå tilvalg (fallback når linja ikke er stemplet i OfferPicker). */
  is_optional: boolean;
  offer_total: number;
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  invoice_split: { startup_pct: number; final_pct: number };
}

/** Map én mal-matchet linje til en preview-rad. */
function matchedLineToPreview(
  l: MatchedBudgetLine,
  idx: number,
  isOptional: boolean,
): PreviewLine {
  const unit = l.template_match?.default_unit ?? l.unit ?? (l.has_time ? "t" : "stk");
  // quantity_hours representerer kun fakturerbare arbeidstimer på interne
  // oppgaver. Eksterne kostnader ("Utstyrsleie 1 d", "Boksmusikk 1 fast")
  // skal ikke summeres som timer i Tilbud/Timetildeling, så vi nuller ut
  // for external og for ikke-tidsbaserte enheter.
  const isHourlyInternal =
    l.effective_cost_type === "internal" && (unit === "t" || unit === "d");
  const quantityHours = isHourlyInternal ? l.quantity_hours ?? 0 : 0;
  return {
    id: `tmp-${idx}`,
    phase: l.effective_phase,
    description: l.description,
    quantity: l.quantity ?? 0,
    unit,
    quantity_hours: quantityHours,
    amount: l.amount,
    cost_type: l.effective_cost_type,
    template_id: l.template_match?.template_id ?? null,
    template_name: l.template_match?.template_name ?? null,
    is_optional: isOptional,
  };
}

/**
 * Bygg preview fra et allerede mal-matchet budsjett (typisk fra OfferPicker,
 * der flere budsjetter kombineres og hver linje får stemplet tilvalg-flagg).
 * Per-linje is_optional vinner; faller tilbake til budsjett-nivå (Vision) når
 * linja ikke er stemplet (ukombinert budsjett).
 */
export function buildPreviewFromMatchedBudget(budget: MatchedBudgetInput): BuildPreviewResult {
  const lines = budget.lines.map((l, idx) =>
    matchedLineToPreview(l, idx, l.is_optional ?? budget.is_optional ?? false),
  );
  if (lines.length === 0) return { lines: [], metadata: null };
  const metadata: PreviewMetadata = {
    offer_total: budget.offer_total,
    overhead_pct: budget.overhead_pct,
    discount_pct: budget.discount_pct,
    discount_amount: budget.discount_amount,
    startup_invoice_pct: budget.invoice_split.startup_pct,
    final_invoice_pct: budget.invoice_split.final_pct,
  };
  return { lines, metadata };
}

/**
 * Bygg preview fra ekstraherte budsjetter (mal-matcher først). Tynn wrapper
 * over buildPreviewFromMatchedBudget for kall som ikke har enriched linjer.
 */
export function buildPreviewFromOffers(
  offers: ExtractedBudget[],
  templates: OfferLineTemplate[],
): BuildPreviewResult {
  if (offers.length === 0) return { lines: [], metadata: null };
  const offer = offers[0];
  return buildPreviewFromMatchedBudget({
    ...offer,
    lines: enrichBudgetLines(offer.lines, templates),
  });
}

/**
 * Holder quantity_hours i sync med cost_type/unit/quantity etter en patch.
 * Eksterne kostnader og ikke-tidsbaserte enheter skal alltid være 0t.
 * Eksportert separat så vi kan unit-teste reglene uten React.
 */
export function applyPreviewLinePatch(line: PreviewLine, patch: Partial<PreviewLine>): PreviewLine {
  const next = { ...line, ...patch };
  const isHourlyInternal =
    next.cost_type === "internal" && (next.unit === "t" || next.unit === "d");
  next.quantity_hours = isHourlyInternal
    ? next.unit === "d"
      ? next.quantity * 8
      : next.quantity
    : 0;
  return next;
}

/**
 * State-hook for ImportBudgetDialog preview-flyten. Eksponerer:
 *   - step (upload | preview)
 *   - linjer + metadata + unmatched count
 *   - reset/finalize/update/remove-callbacks
 *
 * Selve fil-henting og parsing skjer i komponenten (krever useExtractOfferBudget
 * + toast/file-API som ikke hører hjemme i en ren state-hook).
 */
export function useImportPreview() {
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [previewLines, setPreviewLines] = useState<PreviewLine[]>([]);
  const [previewMetadata, setPreviewMetadata] = useState<PreviewMetadata | null>(null);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  const reset = () => {
    setStep("upload");
    setPreviewLines([]);
    setPreviewMetadata(null);
    setUnmatchedCount(0);
  };

  const finalizePreview = (preview: BuildPreviewResult): boolean => {
    if (preview.lines.length === 0) return false;
    setPreviewLines(preview.lines);
    setPreviewMetadata(preview.metadata);
    setUnmatchedCount(preview.lines.filter((l) => l.template_id === null).length);
    setStep("preview");
    return true;
  };

  const updateLine = (id: string, patch: Partial<PreviewLine>) => {
    setPreviewLines((lines) =>
      lines.map((l) => (l.id !== id ? l : applyPreviewLinePatch(l, patch))),
    );
  };

  const removeLine = (id: string) => {
    setPreviewLines((lines) => lines.filter((l) => l.id !== id));
  };

  return {
    step,
    previewLines,
    previewMetadata,
    setPreviewMetadata,
    unmatchedCount,
    reset,
    finalizePreview,
    updateLine,
    removeLine,
  };
}
